'use client';

import { motion } from 'framer-motion';
import { cn } from '@/app/utils';
import type { FeatureHighlightsSectionData } from '../../types';
import { encodeHttpMediaUrl } from '../../utils/media-url';
import { PortableTextContent } from '../PortableTextContent';
import { SectionShell, staggerContainer, staggerItem } from '../section-shared';

export function FeatureHighlightsSection({ section }: { section: FeatureHighlightsSectionData }) {
  const centered = section.layout === 'centered_icon';

  return (
    <SectionShell>
      <div className={cn('w-full text-center', centered ? 'mb-16 sm:mb-10' : 'mb-10 sm:mb-8')}>
        <h2 className="font-semibold text-[36px] text-white leading-[1.2] sm:text-[28px]">{section.title}</h2>
      </div>
      <motion.div
        className={cn('grid grid-cols-3 sm:grid-cols-1', centered ? 'gap-x-8 gap-y-12 sm:gap-y-10' : 'gap-8 sm:gap-4')}
        initial="hidden"
        variants={staggerContainer}
        viewport={{ once: true, margin: '-60px' }}
        whileInView="visible"
      >
        {section.sellingPoints?.map((point) => (
          <motion.div
            key={point._id}
            className={cn(
              'flex flex-col',
              centered ? 'items-center text-center' : 'gap-8 rounded-2xl border-2 border-white/10 p-5',
            )}
            variants={staggerItem}
          >
            <div
              className={cn(
                'flex shrink-0 items-center justify-center rounded-full',
                centered
                  ? 'size-16 bg-gradient-to-br from-[#0086FF]/20 to-[#0086FF]/5'
                  : 'size-12 border-2 border-[#0086FF]',
              )}
            >
              {point.icon?.url && (
                <img
                  alt={point.icon.alt || point.title}
                  className={cn('object-contain', centered ? 'size-[22px]' : 'size-5')}
                  src={encodeHttpMediaUrl(point.icon.url)}
                />
              )}
            </div>
            {centered ? (
              <>
                <h3 className="mt-4 font-semibold text-[17px] text-white leading-[1.4]">{point.title}</h3>
                <PortableTextContent
                  className="mt-2 max-w-[280px] text-center text-sm leading-[1.6]"
                  value={point.description}
                />
              </>
            ) : (
              <div className="flex flex-col gap-2">
                <h3 className="font-semibold text-base text-white leading-6">{point.title}</h3>
                <PortableTextContent className="text-sm leading-[1.5]" value={point.description} />
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>
    </SectionShell>
  );
}
